/**
 * Der Live-Strom einer Sitzung.
 *
 * EventSource kann keine Header setzen; das Token geht als Query-Parameter mit
 * — derselbe Weg, den der Server dafür vorsieht. Bei Abriss verbindet der
 * Browser von selbst neu; der Screen lädt dann einmal frisch nach.
 */
import { useEffect, useRef } from 'react'
import { gespeichertesToken } from './client.js'
import type { TickerMeldung } from './typen.js'

export const STROM_ARTEN = [
  'bereit',
  'check_in',
  'ankuendigung',
  'buchung',
  'tilgung',
  'schnapsuhr_geplant',
  'schnapsuhr_alarm',
  'schnapsuhr_vollstreckt',
  'event_status',
  'battle',
] as const

export function useEventStrom(
  eventId: number | null,
  beiMeldung: (art: string, meldung: TickerMeldung) => void,
): void {
  // Der Handler wandert in eine Ref, damit ein neuer Render den Strom nicht
  // abreißt und neu aufbaut — die Verbindung soll die Sitzung überleben.
  const handler = useRef(beiMeldung)
  handler.current = beiMeldung

  useEffect(() => {
    if (eventId === null) return
    const token = gespeichertesToken()
    if (!token) return

    const quelle = new EventSource(`/api/events/${eventId}/stream?token=${encodeURIComponent(token)}`)

    const hoerer = new Map<string, (e: MessageEvent) => void>()
    for (const art of STROM_ARTEN) {
      const hoere = (e: MessageEvent): void => {
        try {
          handler.current(art, JSON.parse(e.data as string) as TickerMeldung)
        } catch {
          // Eine unlesbare Meldung bringt den Screen nicht um.
        }
      }
      quelle.addEventListener(art, hoere)
      hoerer.set(art, hoere)
    }

    return () => {
      for (const [art, hoere] of hoerer) quelle.removeEventListener(art, hoere)
      quelle.close()
    }
  }, [eventId])
}
